import { HubspotForm } from '@/components/common/hubspot-form';
import { useIntl } from 'Features/Intl/context';
import { createIntlTranslations } from 'Features/Intl/fns';
import { createButtonVariants } from 'Features/SRP/Components/Button';
import { Layout } from 'Features/Shared/Components/Layout';
import { GetServerSidePropsContext } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import _ from 'underscore';

interface Props {
  contactFormId: string;
}

export default function Page({ contactFormId }: Props) {
  const { common, route, __ } = useIntl();
  const endorsement = _.sample(common.endorsements);

  return (
    <Layout>
      <NextSeo title={__('meta.title')} description={__('meta.description')} />

      {/* Content */}
      <div className="max-w-7xl mx-auto min-h-[calc(100vh-6rem)] px-6">
        <div className="bg-white">
          <div className="my-24">
            <div className="relative isolate overflow-hidden bg-slate-50 border border-slate-100 px-6 pt-16 sm:rounded-3xl sm:px-16 md:pt-24 lg:flex lg:gap-x-20 lg:px-24 lg:pt-0">
              <div className="mx-auto max-w-md w-full text-center lg:mx-0 lg:flex-auto lg:py-32 lg:text-left">
                <h2 className="text-4xl font-bold font-space tracking-tight text-slate-900 sm:text-6xl">
                  {route.sections.hero.title}
                </h2>
                <p className="mt-6 text-lg leading-8 text-slate-600">
                  {route.sections.hero.subtitle}
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6 lg:justify-start">
                  <Link href="/estimator">
                    <a className={createButtonVariants({ variant: 'blue', size: 'lg' })}>
                      {route.sections.hero.action}
                    </a>
                  </Link>
                </div>
                <div className="text-slate-500 leading-relaxed text-sm mt-8">
                  {route.sections.hero.disclaimer}
                </div>
              </div>
              <div className="relative w-full h-96 mt-16 sm:mt-0 sm:h-auto flex items-center justify-center">
                <img
                  className="absolute top-0 bottom-0 left-0 right-0 object-cover bg-white/5 ring-1 ring-white/10"
                  src="https://images.unsplash.com/photo-1509744645300-a2098b11871a?q=80&w=1000&auto=format&fit=crop"
                  alt="Sell houses like artworks"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="h-36 w-[2px] bg-slate-200 mx-auto rounded-xl"></div>

        <section className="my-24">
          <div className="max-w-xl mx-auto">
            <p className="text-3xl text-center font-bold font-space tracking-tight text-slate-900 sm:text-4xl">
              {route.sections.advantages.title}
            </p>
            <p className="mt-6 text-lg leading-8 text-center text-slate-600">
              {route.sections.advantages.subtitle}
            </p>
          </div>

          <dl className="grid grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-4 lg:gap-y-16 mt-10">
            {route.sections.advantages.items.map((element: any, idx: number) => (
              <div key={idx}>
                <div className="flex items-center justify-center bg-slate-100 h-10 w-10 rounded-xl mb-3">
                  <span className="text-xl font-semibold">{idx + 1}</span>
                </div>
                <dt className="text-base font-semibold leading-7 text-slate-900">
                  {element.title}
                </dt>
                <dd className="mt-2 text-base leading-7 text-slate-600">{element.subtitle}</dd>
                <Link passHref href={element.anchor}>
                  <a className={createButtonVariants({ variant: 'blue', className: 'mt-5' })}>
                    {element.action}
                  </a>
                </Link>
              </div>
            ))}
          </dl>
        </section>

        <div className="h-36 w-[2px] bg-slate-200 mx-auto rounded-xl"></div>

        <section className="relative isolate overflow-hidden bg-white my-24">
          <div className="mx-auto max-w-2xl lg:max-w-4xl">
            <figure>
              <blockquote className="text-center text-xl font-semibold leading-8 text-slate-900 sm:text-2xl sm:leading-9">
                <p>“{endorsement.description}”</p>
              </blockquote>
              <figcaption className="mt-10">
                <div className="mt-4 flex items-center justify-center space-x-3 text-base">
                  <div className="font-semibold text-slate-900">{endorsement.name}</div>
                  <svg
                    viewBox="0 0 2 2"
                    width="3"
                    height="3"
                    aria-hidden="true"
                    className="fill-slate-900"
                  >
                    <circle cx="1" cy="1" r="1" />
                  </svg>
                  <div className="text-slate-600">{endorsement.outline}</div>
                </div>
              </figcaption>
            </figure>
          </div>
        </section>

        <div className="h-36 w-[2px] bg-slate-200 mx-auto rounded-xl"></div>

        <section className="my-24">
          <div className="max-w-xl mx-auto">
            <p className="text-3xl text-center font-bold font-space tracking-tight text-slate-900 sm:text-4xl">
              {route.sections.contact.title}
            </p>
            <p className="mt-6 text-lg leading-8 text-center text-slate-600">
              {route.sections.contact.subtitle}
            </p>
          </div>

          <div className="mt-10">
            <HubspotForm formId={contactFormId} id="contact-form" />
          </div>
        </section>
      </div>
    </Layout>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const locale = context.locale?.startsWith('it') ? 'it' : 'en';

  const contactFormId =
    locale === 'it'
      ? 'cb9bff70-3e38-457b-801a-be1bd96fd7cd'
      : 'b36067b2-77ea-49b6-a310-f9024c07d8fd';

  const intl = await createIntlTranslations(locale, 'sell');
  const i18n = await serverSideTranslations(locale);

  return {
    props: {
      contactFormId,
      ...intl,
      ...i18n,
    },
  };
};
